/**
 * Remote tool catalog (Phase 3 of the agent tool catalog plan).
 *
 * Fetches `AgentToolCatalogService.ListTools()` once per session and caches
 * the response in a Zustand store. The frontend's hand-written
 * `ToolDisplayConfig`s stay authoritative for function-typed concerns
 * (formatSuccess / isValidOutput / formatInput) — proto can't transport
 * functions. But the *data* half (displayName, icon name, loadingText,
 * emptyText) now comes from the backend, so adding a new shanks tool no
 * longer requires a frontend deploy to render with a real name.
 *
 * Session caching: snapshots to `sessionStorage` keyed by the
 * `catalog_version` hash the backend returns. Re-fetches when the hash
 * changes. Stale entries are still useful — if the backend is
 * unreachable, the snapshot is what we render.
 *
 * Default-on as of Phase 6 — the catalog is the only source of display
 * data, since the baked-in tool-configs were stripped of display fields.
 * Set REACT_APP_DISABLE_REMOTE_CATALOG=1 only when debugging offline
 * behavior; otherwise tool cards will render without names or icons.
 *
 * One in-flight fetch at a time, deduped by the store.
 */ import { create } from 'zustand';
import { agentToolCatalogClient } from '../connect/clients';
import { iconByName } from './icons';
const STORAGE_KEY = 'agent-tool-catalog/v1';
/**
 * Whether the remote catalog flow is active.
 *
 * Default: ON. Phase 6 made the catalog the only source of truth for
 * display data — the baked-in tool-configs were stripped of displayName/
 * icon/loadingText/emptyText and now carry only the function-typed
 * overrides (formatSuccess/isValidOutput/...). Disabling the catalog
 * therefore means the tool card has no name and no icon, which is the
 * worst path; we only support it as a last-ditch escape hatch for
 * debugging offline behavior.
 *
 * Set REACT_APP_DISABLE_REMOTE_CATALOG=1 to opt out. Webpack inlines
 * process.env at build time, matching the rest of thousand-sunny.
 */ export function isRemoteCatalogEnabled() {
    if (typeof process === 'undefined' || !process.env) return true;
    const off = process.env.REACT_APP_DISABLE_REMOTE_CATALOG;
    return !(off === '1' || off === 'true');
}
function tokenCostFromEnum(n) {
    // Mirrors the proto enum order:
    //   0 UNSPECIFIED, 1 LOW, 2 MEDIUM, 3 HIGH.
    switch(n){
        case 1:
            return 'low';
        case 2:
            return 'medium';
        case 3:
            return 'high';
        default:
            return 'unspecified';
    }
}
function descriptorToConfig(d) {
    return {
        id: d.name,
        displayName: d.display?.displayName || undefined,
        icon: iconByName(d.display?.lucideIcon),
        loadingText: d.display?.loadingText || undefined,
        emptyText: d.display?.emptyText || undefined,
        category: d.category || undefined,
        tokenCost: tokenCostFromEnum(d.tokenCost)
    };
}
function readSnapshot() {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch  {
        return null;
    }
}
function writeSnapshot(snapshot) {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch  {
    // sessionStorage can throw (quota, Safari private mode) — swallow.
    // The store still works in-memory, we just don't persist across reloads.
    }
}
function snapshotToConfigs(snapshot) {
    const out = new Map();
    for (const entry of snapshot.configs){
        out.set(entry.id, {
            id: entry.id,
            displayName: entry.displayName,
            icon: iconByName(entry.iconName),
            loadingText: entry.loadingText,
            emptyText: entry.emptyText,
            category: entry.category,
            tokenCost: entry.tokenCost ?? 'unspecified'
        });
    }
    return out;
}
function configsToSnapshot(version, descriptors) {
    return {
        catalogVersion: version,
        configs: descriptors.map((d)=>({
                id: d.name,
                displayName: d.display?.displayName || undefined,
                iconName: d.display?.lucideIcon || undefined,
                loadingText: d.display?.loadingText || undefined,
                emptyText: d.display?.emptyText || undefined,
                category: d.category || undefined,
                tokenCost: tokenCostFromEnum(d.tokenCost)
            }))
    };
}
export const useRemoteCatalog = create((set, get)=>({
        catalogVersion: null,
        configs: new Map(),
        loading: false,
        error: null,
        fetchPromise: null,
        hydrate: ()=>{
            const snapshot = readSnapshot();
            if (!snapshot) return;
            set({
                catalogVersion: snapshot.catalogVersion,
                configs: snapshotToConfigs(snapshot)
            });
        },
        refresh: async ()=>{
            const existing = get().fetchPromise;
            if (existing) {
                return existing;
            }
            set({
                loading: true,
                error: null
            });
            const fetchPromise = (async ()=>{
                try {
                    // Don't request the full input_schema — the frontend never
                    // executes tools, it only renders them. Schemas can be fetched
                    // per-tool via GetTool when something needs them.
                    const resp = await agentToolCatalogClient.listTools({
                        includeInputSchema: false,
                        includeOutputSchema: false
                    });
                    const configs = new Map();
                    for (const d of resp.tools){
                        configs.set(d.name, descriptorToConfig(d));
                    }
                    writeSnapshot(configsToSnapshot(resp.catalogVersion, resp.tools));
                    set({
                        catalogVersion: resp.catalogVersion,
                        configs,
                        loading: false,
                        fetchPromise: null
                    });
                } catch (err) {
                    // Failure path leaves any prior snapshot intact — stale data
                    // beats blank-name fallbacks for the user.
                    set({
                        loading: false,
                        error: err instanceof Error ? err : new Error(String(err)),
                        fetchPromise: null
                    });
                }
            })();
            set({
                fetchPromise
            });
            return fetchPromise;
        }
    }));
/**
 * Lookup a remote tool config by id. Returns `undefined` when the catalog
 * isn't enabled, hasn't loaded yet, or doesn't know about this id. Callers
 * fall back to baked-in configs in that case.
 */ export function getRemoteToolConfig(id) {
    if (!isRemoteCatalogEnabled()) return undefined;
    return useRemoteCatalog.getState().configs.get(id);
}
/**
 * One-shot boot hook: hydrate from sessionStorage, then kick a background
 * refresh. Call this once at the top of the app (e.g. in App.tsx) so the
 * catalog is warm by the time the first tool card renders.
 */ export function initRemoteCatalog() {
    if (!isRemoteCatalogEnabled()) return;
    const state = useRemoteCatalog.getState();
    state.hydrate();
    // Fire-and-forget — UI components consult the store synchronously and
    // re-render when the store updates.
    void state.refresh();
}
